import { Club, ClubJSON } from '../Club/Club';
import { Federacion, FederacionJSON } from '../Federacion/Federacion';

interface UserRoles {
  accessLevelName: string;
  accessLevelDesc: string;
  roleDesc: string;
  roleName: string;
  attributes: { key: string; value: string[] };
}

const accessLevelNameClub = 'club';
const accessLevelNameFederacion = 'fed';
const accessLevelNameConfederacion = 'conf';
const accessLevelNameAdmin = 'admin';
const accessLevelNameArbitro = 'referee';
const supervisor = 'Supervisor';

// RoleNames
const refSup = 'ref-sup';
const refStd = 'ref-std';
const fedTrib = 'fed-trib';
const fedSupervisor = 'fed-supervisor';
const fedStd = 'fed-std';
const fedRespTorneos = 'fed-resp-torneos';
const confTrib = 'conf-trib';
const confSupervisor = 'conf-supervisor';
const confStd = 'conf-std';
const confResp = 'conf-resp';
const clubPlanillero = 'club-planillero';
const clubDeleg = 'club-deleg';
const admin = 'admin';

export interface UsuarioJSON {
  us_id: string | null;
  fullName: string;
  userName: string;
  us_password: string;
  us_accweb: any;
  us_clubid: number;
  us_nivel: number;
  us_es_federacion: any;
  us_fedid: number;
  club: ClubJSON | null;
  federacion: FederacionJSON | null;
  scopes: string[];
  roles: UserRoles[];
}

export class Usuario {
  protected constructor(
    private id: string | null,
    private nombre: string,
    private login: string,
    private password: string,
    private accweb: any,
    private email: string,
    private clubid: number,
    private nivel: number,
    private esFederacion: any,
    private fedid: number,
    private club: Club | null,
    private federacion: Federacion | null,
    private scopes: string[],
    private roles: UserRoles[]
  ) {}

  static createFrom(
    id: string | null,
    nombre: string,
    login: string,
    password: string,
    accweb: any,
    email: string,
    clubid: number,
    nivel: number,
    esFederacion: any,
    fedid: number,
    club: Club | null,
    federacion: Federacion | null,
    scopes: string[],
    roles: UserRoles[]
  ) {
    return new this(
      id,
      nombre,
      login,
      password,
      accweb,
      email,
      clubid,
      nivel,
      esFederacion,
      fedid,
      club,
      federacion,
      scopes,
      roles
    );
  }

  isIdentifiedBy(anId: string): boolean {
    return this.id === anId;
  }

  isIdentifiedAs(aEntity: Usuario) {
    if (this.id) {
      return aEntity.isIdentifiedBy(this.id);
    } else {
      return false;
    }
  }

  static fromJSON(entity: UsuarioJSON) {
    const ret = Usuario.createFrom(
      entity.us_id,
      entity.fullName,
      entity.userName,
      entity.us_password,
      entity.us_accweb,
      entity.userName,
      entity.us_clubid,
      entity.us_nivel,
      entity.us_es_federacion,
      entity.us_fedid,
      entity.club ? Club.fromJSON(entity.club) : null,
      entity.federacion ? Federacion.fromJSON(entity.federacion) : null,
      entity.scopes,
      entity.roles
    );
    return ret;
  }

  toJSON(): UsuarioJSON {
    return {
      us_id: this.getId(),
      fullName: this.getNombre(),
      userName: this.getLogin(),
      us_password: this.getPassword(),
      us_accweb: this.getAccweb(),
      us_clubid: this.getClubid(),
      us_nivel: this.getNivel(),
      us_es_federacion: this.getEsFederacion(),
      us_fedid: this.getFedid(),
      club: this.club ? this.club.toJSON() : null,
      federacion: this.federacion ? this.federacion.toJSON() : null,
      scopes: [],
      roles: [],
    };
  }

  getId(): string | null {
    return this.id;
  }

  getNombre(): string {
    return this.nombre;
  }

  getBreadcrumb(): string {
    let ret = '';
    if (this.federacion) {
      ret += this.federacion?.getSigla();
    }

    if (this.club) {
      ret += ' - ' + this.club?.getNombre();
    }
    return ret;
  }

  getLogin(): string {
    return this.login;
  }

  getPassword(): string {
    return this.password;
  }

  getAccweb(): any {
    return this.accweb;
  }

  getEmail(): string {
    return this.email;
  }

  getClubid(): number {
    return this.clubid;
  }

  getNivel(): number {
    return this.nivel;
  }

  getEsFederacion(): any {
    return this.esFederacion;
  }

  getFedid(): number {
    return this.fedid;
  }

  getClub(): Club | null {
    return this.club;
  }

  getFederacion(): Federacion | null {
    return this.federacion;
  }

  getRoles(): string[] {
    return this.scopes;
  }

  setId(value: string | null): void {
    this.id = value;
  }

  setNombre(value: string): void {
    this.nombre = value;
  }

  setLogin(value: string): void {
    this.login = value;
  }

  setPassword(value: string): void {
    this.password = value;
  }

  setAccweb(value: any): void {
    this.accweb = value;
  }

  setEmail(value: string): void {
    this.email = value;
  }

  setClubid(value: number): void {
    this.clubid = value;
  }

  setNivel(value: number): void {
    this.nivel = value;
  }

  setEsFederacion(value: any): void {
    this.esFederacion = value;
  }

  setFedid(value: number): void {
    this.fedid = value;
  }

  setClub(value: Club): void {
    this.club = value;
  }

  setFederacion(value: Federacion): void {
    this.federacion = value;
  }

  isConfederationUser(): boolean {
    return this.roles.some((r) => r.accessLevelName === accessLevelNameConfederacion);
    // return false;
  }

  isConfederacionResponsableUser(): boolean {
    return this.roles.some((r) => r.roleName === confResp);
  }

  isConfederacionSupervisorUser(): boolean {
    return this.roles.some((r) => r.roleName === confSupervisor);
  }

  isFederationUser(): boolean {
    return this.roles.some((r) => r.accessLevelName === accessLevelNameFederacion);
    // const ret = !!this.federacion && !this.isClubUser();
    // return ret;
  }

  isFederacionResponsableTorneosUser(): boolean {
    return this.roles.some((r) => r.roleName === fedRespTorneos);
  }

  isFederacionSupervisorUser(): boolean {
    return this.roles.some((r) => r.roleName === fedSupervisor);
  }

  isClubUser(): boolean {
    return this.roles.some((r) => r.accessLevelName === accessLevelNameClub);
    // return !!this.club;
  }

  isAdminUser(): boolean {
    return this.roles.some((r) => r.accessLevelName === accessLevelNameAdmin);
    // return !!this.club;
  }

  isArbitroUser(): boolean {
    return this.roles.some((r) => r.accessLevelName === accessLevelNameArbitro);
  }

  isSupervisorUser(): boolean {
    return this.roles.some((r) => r.roleDesc === supervisor);
  }

  hasScope(scopes: string[]): boolean {
    return this.scopes.some((scope) => scopes.includes(scope) || scope === 'admin');
  }
}
